import { useState, useEffect, useCallback } from 'react';
import { gapi } from 'gapi-script';

const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const scopes = [
  'https://www.googleapis.com/auth/calendar.app.created',
  'https://www.googleapis.com/auth/calendar.calendarlist.readonly',
];

/**
 * Custom hook for interacting with Google Calendar API
 * @param {Object} config - Configuration options
 * @param {boolean} config.autoSignIn - Whether to automatically sign in (default: false)
 * @returns {Object} - Google Calendar API methods and state
 */
const useGoogleCalendar = (config = {}) => {

  const {
    autoSignIn = true,
  } = config;

  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [calendars, setCalendars] = useState([]);


  // Sign in the user
  const signIn = useCallback(async () => {
    console.log('sign in hook');
    if (!isInitialized) {
      setError(new Error('Google API client not initialized'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await gapi.auth2.getAuthInstance().signIn();
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized]);

  // Initialize the Google API client
  useEffect(() => {
    console.log('init client hook');
    if (!apiKey || !clientId) {
      console.log('API Key and Client ID are required');
      setError(new Error('API Key and Client ID are required'));
      return;
    }

    const initClient = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const gapiLoadResp = await gapi.load('client:auth2', async () => {
          try {
            const gapiClientInitResp = await gapi.client.init({
              apiKey,
              clientId,
              scope: scopes.join(' '),
              discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'],
            });
            console.log('gapiClientInitResp', gapiClientInitResp);

            // Listen for sign-in state changes
            gapi.auth2.getAuthInstance().isSignedIn.listen((signedIn) => {
              setIsSignedIn(signedIn);
            });

            // Set the initial sign-in state
            setIsSignedIn(gapi.auth2.getAuthInstance().isSignedIn.get());
            setIsInitialized(true);

            if (autoSignIn && !gapi.auth2.getAuthInstance().isSignedIn.get()) {
              await signIn();
            }
          } catch (error) {
            setError(error);
          } finally {
            setIsLoading(false);
          }
        });

        console.log('gapiLoadResp', gapiLoadResp);

      } catch (error) {
        setError(error);
        setIsLoading(false);
      }
    };

    initClient();
  }, [autoSignIn, signIn]);

  // Sign out the user
  const signOut = useCallback(async () => {
    if (!isInitialized) {
      setError(new Error('Google API client not initialized'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await gapi.auth2.getAuthInstance().signOut();
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized]);

  // Fetch user's calendars
  const fetchCalendars = useCallback(async () => {
    if (!isInitialized || !isSignedIn) {
      setError(new Error('User not signed in'));
      return [];
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await gapi.client.calendar.calendarList.list();
      const calendarList = response.result.items;
      setCalendars(calendarList);
      return calendarList;
    } catch (error) {
      setError(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, isSignedIn]);

  // Fetch events from a specific calendar
  const fetchEvents = useCallback(async (calendarId = 'primary', params = {}) => {
    if (!isInitialized || !isSignedIn) {
      setError(new Error('User not signed in'));
      return [];
    }

    setIsLoading(true);
    setError(null);

    const defaultParams = {
      calendarId,
      timeMin: new Date().toISOString(),
      maxResults: 10,
      singleEvents: true,
      orderBy: 'startTime',
      ...params,
    };

    try {
      const response = await gapi.client.calendar.events.list(defaultParams);
      const eventList = response.result.items;
      setEvents(eventList);
      return eventList;
    } catch (error) {
      setError(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, isSignedIn]);

  // Create a new event
  const createEvent = useCallback(async (calendarId = 'primary', event) => {
    if (!isInitialized || !isSignedIn) {
      setError(new Error('User not signed in'));
      return null;
    }

    if (!event) {
      setError(new Error('Event data is required'));
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await gapi.client.calendar.events.insert({
        calendarId,
        resource: event,
      });

      // Update events list if successful
      await fetchEvents(calendarId);

      return response.result;
    } catch (error) {
      setError(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, isSignedIn, fetchEvents]);

  // Update an existing event
  const updateEvent = useCallback(async (calendarId = 'primary', eventId, updatedEvent) => {
    if (!isInitialized || !isSignedIn) {
      setError(new Error('User not signed in'));
      return null;
    }

    if (!eventId || !updatedEvent) {
      setError(new Error('Event ID and updated event data are required'));
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await gapi.client.calendar.events.update({
        calendarId,
        eventId,
        resource: updatedEvent,
      });

      // Update events list if successful
      await fetchEvents(calendarId);

      return response.result;
    } catch (error) {
      setError(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, isSignedIn, fetchEvents]);

  // Delete an event
  const deleteEvent = useCallback(async (calendarId = 'primary', eventId) => {
    if (!isInitialized || !isSignedIn) {
      setError(new Error('User not signed in'));
      return false;
    }

    if (!eventId) {
      setError(new Error('Event ID is required'));
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      await gapi.client.calendar.events.delete({
        calendarId,
        eventId,
      });

      // Update events list if successful
      await fetchEvents(calendarId);

      return true;
    } catch (error) {
      setError(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, isSignedIn, fetchEvents]);

  return {
    // State
    isInitialized,
    isLoading,
    isSignedIn,
    error,
    events,
    calendars,

    // Authentication methods
    signIn,
    signOut,

    // Calendar methods
    fetchCalendars,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
  };
};

export default useGoogleCalendar;
